import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { useToast } from '../hooks/useToast';

const ExpiryTracking: React.FC = () => {
  const { showToast } = useToast();
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState<any>(null);
  const [batches, setBatches] = useState<any[]>([{ batch_no: '', expiry_date: '', qty: '' }]);

  const fmt = (n: any) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const load = () => {
    setLoading(true);
    api.get('/inventory/untracked', { params: { search: search || undefined, limit: 300 } })
      .then(r => setRows(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line

  const open = (r: any) => {
    setTarget(r);
    setBatches([{ batch_no: '', expiry_date: '', qty: String(Number(r.untracked)) }]);
  };

  const entered = batches.reduce((s, b) => s + Number(b.qty || 0), 0);

  const save = async () => {
    const clean = batches.filter(b => b.expiry_date && Number(b.qty) > 0);
    if (!clean.length) { showToast('Each line needs an expiry date and a quantity', 'error'); return; }
    if (entered > Number(target.untracked) + 0.0001) { showToast(`Only ${Number(target.untracked)} units are untracked`, 'error'); return; }
    setSaving(true);
    try {
      const r = await api.post('/inventory/track', {
        product_id: Number(target.product_id), location_id: Number(target.location_id),
        batches: clean.map(b => ({ batch_no: b.batch_no || null, expiry_date: b.expiry_date, qty: Number(b.qty) })),
      });
      showToast(r.data.message);
      setTarget(null); load();
    } catch (e: any) { showToast(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const th: React.CSSProperties = { padding: '8px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase' };

  if (target) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Record expiry — {target.product_name}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setTarget(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 12 }}>
        {target.location_name} · on hand <strong>{fmt(target.qty_on_hand)}</strong> · untracked <strong>{fmt(target.untracked)}</strong>
      </div>
      <div className="card">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: 'var(--sur2)' }}>
            <th style={{ ...th, textAlign: 'left' }}>Batch no. (optional)</th>
            <th style={{ ...th, textAlign: 'left' }}>Expiry date</th>
            <th style={{ ...th, textAlign: 'right' }}>Quantity</th>
            <th style={{ width: 40 }}></th>
          </tr></thead>
          <tbody>
            {batches.map((b, i) => (
              <tr key={i}>
                <td style={{ padding: 6 }}><input className="form-control" value={b.batch_no} onChange={e => setBatches(x => x.map((y, j) => j === i ? { ...y, batch_no: e.target.value } : y))} /></td>
                <td style={{ padding: 6, width: 190 }}><input type="date" className="form-control" value={b.expiry_date} onChange={e => setBatches(x => x.map((y, j) => j === i ? { ...y, expiry_date: e.target.value } : y))} /></td>
                <td style={{ padding: 6, width: 140 }}><input className="form-control mono" style={{ textAlign: 'right' }} value={b.qty} onChange={e => setBatches(x => x.map((y, j) => j === i ? { ...y, qty: e.target.value } : y))} /></td>
                <td style={{ padding: 6 }}>{batches.length > 1 && <button className="btn btn-secondary btn-sm" onClick={() => setBatches(x => x.filter((_, j) => j !== i))}>x</button>}</td>
              </tr>
            ))}
            <tr><td colSpan={4} style={{ padding: 8, display: 'flex', justifyContent: 'space-between' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setBatches(x => [...x, { batch_no: '', expiry_date: '', qty: '' }])}>+ Add expiry date</button>
              <span style={{ fontSize: 12, color: entered > Number(target.untracked) + 0.0001 ? 'var(--red)' : 'var(--tx3)' }}>
                Entered {fmt(entered)} of {fmt(target.untracked)}
              </span>
            </td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Expiry Tracking</h1></div>
      <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 12 }}>
        Stock on hand with no expiry recorded. Enter the expiry from the pack so it appears in the expiry report and warns at the till.
      </div>
      <div className="filter-bar" style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input className="form-control" style={{ maxWidth: 320 }} placeholder="Search product or SKU..." value={search}
          onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} />
        <button className="btn btn-secondary btn-sm" onClick={load}>Search</button>
        <span className="record-count" style={{ marginLeft: 'auto' }}>{rows.length} lines</span>
      </div>
      <div className="card">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: 'var(--sur2)' }}>
            <th style={{ ...th, textAlign: 'left' }}>SKU</th>
            <th style={{ ...th, textAlign: 'left' }}>Product</th>
            <th style={{ ...th, textAlign: 'left' }}>Location</th>
            <th style={{ ...th, textAlign: 'right' }}>On hand</th>
            <th style={{ ...th, textAlign: 'right' }}>Tracked</th>
            <th style={{ ...th, textAlign: 'right' }}>Untracked</th>
            <th></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--tx3)' }}>Loading...</td></tr>
            : rows.length === 0 ? <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--tx3)' }}>All stock has an expiry recorded.</td></tr>
            : rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--bdr)' }}>
                <td className="td-code" style={{ padding: '8px 10px' }}>{r.sku}</td>
                <td style={{ padding: '8px 10px', fontSize: 12 }}>{r.product_name}{r.has_expiry ? '' : <span style={{ fontSize: 10, color: 'var(--tx3)' }}> · not flagged</span>}</td>
                <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--tx3)' }}>{r.location_name}</td>
                <td className="td-num" style={{ padding: '8px 10px' }}>{fmt(r.qty_on_hand)}</td>
                <td className="td-num" style={{ padding: '8px 10px', color: 'var(--tx3)' }}>{fmt(r.tracked)}</td>
                <td className="td-num" style={{ padding: '8px 10px', color: 'var(--amber)' }}>{fmt(r.untracked)}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => open(r)}>Record expiry</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExpiryTracking;
